import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getPermissions, getUser, fetchUserPermissions, clearSession, hasUsablePermissionData, fetchUserById } from '../../services/auth';
import { CircularProgress, Box, Typography, Paper, Container } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { RESOURCE_DISPLAY_NAMES } from '../../utils/resourceMapping';

const normalize = (value) => String(value || '').trim().toLowerCase();

const getPermissionResourceNames = (permission) => [
  permission?.resource?.displayName,
  permission?.resource?.resourceKey,
  permission?.resource?.name,
  permission?.resource_display_name,
  permission?.resource_name,
  permission?.resourceKey,
].filter(Boolean);

const getPermissionActionNames = (permission) => [
  permission?.action?.name,
  permission?.action_name,
  permission?.actionName,
].filter(Boolean);

const summarizePermissions = (permissions = []) => ({
  count: Array.isArray(permissions) ? permissions.length : 0,
  missingResource: Array.isArray(permissions) ? permissions.filter((perm) => !perm?.resource).length : 0,
  missingAction: Array.isArray(permissions) ? permissions.filter((perm) => !perm?.action).length : 0,
  resources: Array.isArray(permissions)
    ? [...new Set(permissions.flatMap(getPermissionResourceNames).map((name) => String(name).trim()).filter(Boolean))]
    : [],
  actions: Array.isArray(permissions)
    ? [...new Set(permissions.flatMap(getPermissionActionNames).map((name) => String(name).trim()).filter(Boolean))]
    : [],
});

/**
 * RouteGuard - Advanced route protection with permission checking
 * 
 * Usage:
 * <Route 
 *   path="/dashboard" 
 *   element={
 *     <RouteGuard requiredResources={['overview']}>
 *       <Dashboard />
 *     </RouteGuard>
 *   } 
 * />
 */
const RouteGuard = ({
  children,
  requiredResources = [],
  requiredAction = 'View',
  requireAll = true,
  fallback = null,
  isPublic = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [reason, setReason] = useState('');
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // If route is public, allow access
        if (isPublic) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Check if user is authenticated
        if (!isAuthenticated()) {
          setHasAccess(false);
          setReason('Not authenticated');
          setLoading(false);
          return;
        }

        // Token expiration is server-managed with HttpOnly cookies
        // No client-side check needed

        // Fetch user and permissions
        let user = getUser();
        try {
          const apiUser = await fetchUserById();
          if (apiUser) user = apiUser;
        } catch (e) {
          console.warn('Could not fetch full user data:', e);
        }
        
        let permissions = getPermissions();
        console.log('[RBAC][RouteGuard] Starting access check', {
          path: window.location.pathname,
          requiredResources,
          requiredAction,
          requireAll,
          cached: summarizePermissions(permissions || []),
        });

        if (!permissions || permissions.length === 0 || !hasUsablePermissionData(permissions)) {
          if (permissions?.length > 0) {
            console.warn('[RBAC][RouteGuard] Cached permissions are stale or missing resource/action details; refetching', summarizePermissions(permissions));
          }
          if (user?.userId) {
            permissions = await fetchUserPermissions(user.userId);
          }
        }

        console.log('[RBAC][RouteGuard] Permissions ready', summarizePermissions(permissions || []));
        
        // Check if Super Admin with NON-global/non-all scope trying to access Settings only (not User Management)
        const isSuperAdminWithoutGlobalScope = 
          user?.role?.name === "Super Admin" && 
          user?.scope?.name && // Only hide if scope exists
          !["global", "all"].includes(user?.scope?.name?.toLowerCase());
        
        const isAccessingSettings = requiredResources.includes(RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS);
        
        if (isSuperAdminWithoutGlobalScope && isAccessingSettings) {
          setHasAccess(false);
          setReason('You do not have permission to access this page.');
          setLoading(false);
          return;
        }

        // If no specific resources are required, just check authentication
        if (!requiredResources || requiredResources.length === 0) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Check permissions based on requirement type
        const checkResults = requiredResources.map(resourceName => {
          const result = permissions && permissions.some(perm => {
            const resourceMatch = getPermissionResourceNames(perm).some((name) => normalize(name) === normalize(resourceName));
            const actionMatch = getPermissionActionNames(perm).some((name) => normalize(name) === normalize(requiredAction));
            if (resourceMatch || actionMatch) {
              console.log('[RBAC][RouteGuard] Permission candidate', {
                requiredResource: resourceName,
                requiredAction,
                resourceNames: getPermissionResourceNames(perm),
                actionNames: getPermissionActionNames(perm),
                resourceMatch,
                actionMatch,
              });
            }
            return resourceMatch && actionMatch;
          });
          console.log('[RBAC][RouteGuard] Resource check result', {
            requiredResource: resourceName,
            requiredAction,
            result: Boolean(result),
          });
          return result;
        });

        // Determine if user has access
        let hasPermission = requireAll
          ? checkResults.every(result => result)
          : checkResults.some(result => result);

        setHasAccess(Boolean(hasPermission));

        if (!hasPermission) {
          console.warn('[RBAC][RouteGuard] Access denied', {
            requiredResources,
            requiredAction,
            checkResults,
            permissions: summarizePermissions(permissions || []),
          });
          setReason(requireAll
            ? `You need ${requiredAction} access to all required resources`
            : `You need ${requiredAction} access to at least one resource`
          );
        }
      } catch (error) {
        console.error('Error checking access:', error);
        setHasAccess(false);
        setReason('Error checking permissions');
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [requiredResources, requiredAction, requireAll, isPublic, location.pathname]);

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated()) {
    clearSession();
    return <Navigate to="/login" replace />;
  }

  // No permission to access this route
  if (!hasAccess) {
    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Default permission denied view
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
            }}
          >
            <LockIcon
              sx={{
                fontSize: 48,
                color: '#ef5350',
                mb: 2,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {reason || 'You do not have permission to access this page.'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Contact your administrator if you believe this is a mistake.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Access granted
  return children;
};

export default RouteGuard;
